/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { ImageSearch } from '@mui/icons-material'
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'

import {
  getCropImageStyle,
  getCropOverlayStyle,
  type NaturalImageSize,
} from './classificationUtils'
import {
  formatCreatedDate,
  formatScore,
  formatToneLabel,
  formatUltraCompanyChoices,
  getBoxDisplayUpc,
  getExplorerBoxColor,
  getExplorerBoxTone,
  getImageToneCounts,
  getProductLabel,
  hasProductDetails,
  toneStyles,
  type ExplorerProduct,
  type LabelingExplorerBox,
  type LabelingExplorerImage,
  type LabelingExplorerItem,
} from './explorerUtils'
import { formatRangeValue, readRangeEndpoint } from './explorerUrlParams'

export const BoxPredictionTooltip = ({
  box,
  productsByUpc,
  children,
}: {
  box: LabelingExplorerBox
  productsByUpc: Record<string, ExplorerProduct>
  children: React.ReactElement
}) => {
  const topPredictions = [...box.predictedUpcs]
    .sort((left, right) => {
      const leftRank = left.classifierRank ?? Number.MAX_SAFE_INTEGER
      const rightRank = right.classifierRank ?? Number.MAX_SAFE_INTEGER
      return leftRank - rightRank
    })
    .slice(0, 5)

  return (
    <Tooltip
      arrow
      placement="bottom-start"
      slotProps={{
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
        },
      }}
      title={
        topPredictions.length ? (
          <Stack spacing={0.75} sx={{ maxWidth: 360, py: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Top 5 predictions
            </Typography>
            {topPredictions.map((prediction, index) => (
              <Box key={`${prediction.upc}-${prediction.displayIndex}`}>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  {index + 1}. {getProductLabel(prediction.upc, productsByUpc)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'grey.300' }}
                >
                  UPC {prediction.upc}
                  {!hasProductDetails(prediction.upc, productsByUpc)
                    ? ' · no product match'
                    : ''}{' '}
                  · score {formatScore(prediction.classifierScore)}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          'No predictions'
        )
      }
    >
      {children}
    </Tooltip>
  )
}

export const ProductChip = ({
  box,
  productsByUpc,
}: {
  box: LabelingExplorerBox
  productsByUpc: Record<string, ExplorerProduct>
}) => {
  const upc = getBoxDisplayUpc(box)
  return (
    <BoxPredictionTooltip box={box} productsByUpc={productsByUpc}>
      <Chip
        size="small"
        label={getProductLabel(upc, productsByUpc)}
        color={getExplorerBoxColor(getExplorerBoxTone(box, null))}
        variant="outlined"
        sx={{
          maxWidth: '100%',
          '.MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
      />
    </BoxPredictionTooltip>
  )
}

export const RangeFilter = ({
  label,
  minValue,
  maxValue,
  min,
  max,
  step,
  disabled = false,
  onChange,
}: {
  label: string
  minValue: string
  maxValue: string
  min: number
  max: number
  step: number
  disabled?: boolean
  onChange: (minValue: string, maxValue: string) => void
}) => {
  const sliderMin = readRangeEndpoint(minValue, min, min, max)
  const sliderMax = readRangeEndpoint(maxValue, max, min, max)
  const sliderValue: [number, number] = [
    Math.min(sliderMin, sliderMax),
    Math.max(sliderMin, sliderMax),
  ]
  const [draftValue, setDraftValue] =
    React.useState<[number, number]>(sliderValue)

  React.useEffect(() => {
    setDraftValue(sliderValue)
  }, [sliderValue[0], sliderValue[1]])

  return (
    <Box
      sx={{
        opacity: disabled ? 0.5 : 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        p: 1,
        minWidth: 0,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Slider
        size="small"
        min={min}
        max={max}
        step={step}
        value={draftValue}
        disabled={disabled}
        onChange={(_, value) => {
          if (!Array.isArray(value)) return
          setDraftValue([value[0], value[1]])
        }}
        onChangeCommitted={(_, value) => {
          if (!Array.isArray(value)) return
          const [nextMin, nextMax] = value
          onChange(
            nextMin === min ? '' : formatRangeValue(nextMin, step),
            nextMax === max ? '' : formatRangeValue(nextMax, step),
          )
        }}
        sx={{
          display: 'block',
          width: 'calc(100% - 24px)',
          mx: 1.5,
          mt: -0.25,
          mb: -0.75,
        }}
      />
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          value={minValue}
          disabled={disabled}
          placeholder={formatRangeValue(min, step)}
          onChange={event => onChange(event.target.value, maxValue)}
          inputProps={{ inputMode: 'decimal' }}
          sx={{ minWidth: 0, flex: 1 }}
        />
        <Typography variant="caption" color="text.secondary">
          to
        </Typography>
        <TextField
          size="small"
          value={maxValue}
          disabled={disabled}
          placeholder={formatRangeValue(max, step)}
          onChange={event => onChange(minValue, event.target.value)}
          inputProps={{ inputMode: 'decimal' }}
          sx={{ minWidth: 0, flex: 1 }}
        />
      </Stack>
    </Box>
  )
}

export const ImageSummaryCard = ({
  item,
  closeConfidenceThreshold,
  onOpen,
}: {
  item: LabelingExplorerItem
  closeConfidenceThreshold: number | null
  onOpen: (image: LabelingExplorerImage) => void
}) => {
  const toneCounts = getImageToneCounts(item.image, closeConfidenceThreshold)

  return (
    <Card sx={{ height: '100%', borderRadius: 1 }}>
      <CardActionArea
        onClick={() => onOpen(item.image)}
        sx={{ height: '100%' }}
      >
        <Box
          sx={{
            aspectRatio: '4 / 3',
            bgcolor: 'grey.100',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            component="img"
            src={item.image.imageUrl}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
        <CardContent>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} rowGap={1} flexWrap="wrap">
              <Chip
                size="small"
                label={formatUltraCompanyChoices(item.image.sourceCustomer)}
              />
              <Chip size="small" label={`${item.image.boxCount} boxes`} />
              <Chip
                size="small"
                label={`${toneCounts.unidentified} unidentified`}
                color={getExplorerBoxColor('unidentified')}
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${toneCounts.close} close`}
                color={getExplorerBoxColor('close')}
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${toneCounts.unverified} unverified`}
                color={getExplorerBoxColor('unverified')}
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${toneCounts.verified} verified`}
                color={getExplorerBoxColor('verified')}
                variant="outlined"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Image {item.image.id} · Created{' '}
              {formatCreatedDate(item.image.created)} · score{' '}
              {formatScore(item.image.acquisitionScore)}
            </Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

export const CropSummaryCard = ({
  item,
  productsByUpc,
  closeConfidenceThreshold,
  tileSize,
  onOpen,
  onUseAsSearch,
}: {
  item: LabelingExplorerItem
  productsByUpc: Record<string, ExplorerProduct>
  closeConfidenceThreshold: number | null
  tileSize: number
  onOpen: (image: LabelingExplorerImage, box: LabelingExplorerBox) => void
  onUseAsSearch?: () => void
}) => {
  const box = item.box
  const [naturalSize, setNaturalSize] = React.useState<NaturalImageSize | null>(
    null,
  )

  if (!box) return null

  const tone = getExplorerBoxTone(box, closeConfidenceThreshold)
  const style = toneStyles[tone]
  const cropViewport = { width: tileSize, height: tileSize }

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardActionArea onClick={() => onOpen(item.image, box)} sx={{ flex: 1 }}>
        <Box
          sx={
            {
              width: tileSize,
              height: tileSize,
              bgcolor: 'grey.100',
              overflow: 'hidden',
              position: 'relative',
            } as const
          }
        >
          <Box
            component="img"
            src={item.image.imageUrl}
            alt=""
            loading="lazy"
            onLoad={event => {
              setNaturalSize({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              })
            }}
            sx={
              naturalSize
                ? getCropImageStyle(box, naturalSize, cropViewport)
                : { width: '100%', height: '100%', objectFit: 'cover' }
            }
          />
          {naturalSize && (
            <Box
              sx={{
                ...getCropOverlayStyle(box, naturalSize, cropViewport),
                bgcolor: style.background,
                border: 0,
                pointerEvents: 'none',
              }}
            />
          )}
          {onUseAsSearch && (
            <Tooltip title="Use as visual search">
              <Box
                component="span"
                onClick={e => {
                  e.stopPropagation()
                  onUseAsSearch()
                }}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'rgba(0,0,0,0.45)',
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  zIndex: 2,
                }}
              >
                <ImageSearch sx={{ fontSize: 16 }} />
              </Box>
            </Tooltip>
          )}
        </Box>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              <Chip
                size="small"
                label={formatToneLabel(tone)}
                color={getExplorerBoxColor(tone)}
                variant="outlined"
                sx={{ height: 22 }}
              />
              <Chip
                size="small"
                label={formatScore(box.acquisitionScore)}
                sx={{ height: 22 }}
              />
            </Stack>
            <ProductChip box={box} productsByUpc={productsByUpc} />
            <Typography variant="caption" color="text.secondary" noWrap>
              Image {item.image.id} · crop {box.id}
            </Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

export const VisualSearchCropCard = React.memo(
  ({
    item,
    selected,
    setSelectedBoxIds,
    onOpen,
    productsByUpc,
    closeConfidenceThreshold,
    tileSize,
  }: {
    item: LabelingExplorerItem
    selected: boolean
    setSelectedBoxIds: React.Dispatch<React.SetStateAction<Set<string>>>
    onOpen: (image: LabelingExplorerImage, box: LabelingExplorerBox) => void
    productsByUpc: Record<string, ExplorerProduct>
    closeConfidenceThreshold: number | null
    tileSize: number
  }) => {
    const box = item.box
    const [naturalSize, setNaturalSize] =
      React.useState<NaturalImageSize | null>(null)

    const handleToggle = React.useCallback(() => {
      if (!box) return
      setSelectedBoxIds(prev => {
        const next = new Set(prev)
        if (next.has(box.id)) next.delete(box.id)
        else next.add(box.id)
        return next
      })
    }, [box, setSelectedBoxIds])

    const handleOpen = React.useCallback(() => {
      if (box) onOpen(item.image, box)
    }, [box, item.image, onOpen])

    if (!box) return null

    const tone = getExplorerBoxTone(box, closeConfidenceThreshold)
    const style = toneStyles[tone]
    const cropViewport = { width: tileSize, height: tileSize }

    return (
      <Card
        onClick={handleOpen}
        sx={{
          height: '100%',
          borderRadius: 1,
          cursor: 'pointer',
          outline: selected ? '3px solid' : '1px solid transparent',
          outlineColor: selected ? 'primary.main' : undefined,
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              width: tileSize,
              height: tileSize,
              bgcolor: 'grey.100',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Box
              component="img"
              src={item.image.imageUrl}
              alt=""
              loading="lazy"
              onLoad={event => {
                setNaturalSize({
                  width: event.currentTarget.naturalWidth,
                  height: event.currentTarget.naturalHeight,
                })
              }}
              sx={
                naturalSize
                  ? getCropImageStyle(box, naturalSize, cropViewport)
                  : { width: '100%', height: '100%', objectFit: 'cover' }
              }
            />
            {naturalSize && (
              <Box
                sx={{
                  ...getCropOverlayStyle(box, naturalSize, cropViewport),
                  bgcolor: style.background,
                  border: 0,
                  pointerEvents: 'none',
                }}
              />
            )}
          </Box>
          <Checkbox
            checked={selected}
            size="small"
            onClick={e => e.stopPropagation()}
            onChange={handleToggle}
            sx={{
              position: 'absolute',
              top: 2,
              left: 2,
              bgcolor: 'rgba(255,255,255,0.85)',
              borderRadius: 0.5,
              p: 0.25,
            }}
          />
        </Box>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Stack spacing={0.75}>
            <Chip
              size="small"
              label={formatToneLabel(tone)}
              color={getExplorerBoxColor(tone)}
              variant="outlined"
              sx={{ height: 22 }}
            />
            <ProductChip box={box} productsByUpc={productsByUpc} />
            <Typography variant="caption" color="text.secondary" noWrap>
              Image {item.image.id} · crop {box.id}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    )
  },
)
